/**
 * BeforeUnloadGuard
 *
 * Helper for preventing accidental page/tab unload during
 * critical user flows (e.g. Apple Pay / Google Pay payment processing).
 *
 * This module attaches a 'beforeunload' event listener to 'window' that
 * triggers the native browser confirmation dialog when the user attempts
 * to close, refresh, or navigate away from the page.
 *
 * Usage:
 * '''
 * import { startBeforeUnloadGuard, stopBeforeUnloadGuard } from 'core/toolbox/beforeUnloadGuard';
 *
 * // When critical processing starts
 * startBeforeUnloadGuard();
 *
 * try {
 *   await processPayment();
 * } finally {
 *   // Always disable guard after flow completion
 *   stopBeforeUnloadGuard();
 * }
 * '''
 */

const GUARD_TIMEOUT_MS = 5000; // 5 seconds safety timeout

let guardActive = false;
let guardTimeoutId = -1;

/**
 * @description beforeunload event handler
 * @param e - BeforeUnloadEvent
 */
const beforeUnloadGuard = (e: BeforeUnloadEvent) => {
    if (!guardActive) {
        return;
    }

    e.preventDefault();
    e.returnValue = '';
};

/**
 * @description Sets the guard active state and attaches/removes the event listener accordingly.
 * @param isOn - boolean indicating whether to activate or deactivate the guard
 */
function setGuardActive(isOn: boolean): void {
    guardActive = isOn;

    if (isOn) {
        window.addEventListener('beforeunload', beforeUnloadGuard);
    } else {
        window.removeEventListener('beforeunload', beforeUnloadGuard);
    }
}

/**
 * @description Starts a timeout that will automatically disable the guard after a predefined duration.
 */
function startGuardTimeout(): void {
    clearTimeout(guardTimeoutId);
    guardTimeoutId = window.setTimeout(() => setGuardActive(false), GUARD_TIMEOUT_MS);
}

/**
 * @description Start before-unload guard (prevents accidental tab close/refresh/navigation)
 * and starts a safety timeout that auto-disables the guard.
 */
export function startBeforeUnloadGuard(): void {
    setGuardActive(true);
    startGuardTimeout();
}

/**
 * @description Stop before-unload guard and clears the safety timeout.
 */
export function stopBeforeUnloadGuard(): void {
    setGuardActive(false);
    clearTimeout(guardTimeoutId);
    guardTimeoutId = -1;
}
